/* eslint-disable array-bracket-newline */
import { useEffect, useState } from 'react';
import { Unit } from 'wagmi';
import BigNumber from 'bignumber.js';
import { getNetworkFlags } from '../wallet/helpers';
import { fetchEVMBalance } from '../helpers/fetchEVMBalance';
import { fetchElrondBalance } from '../helpers/fetchElrondBalance';
import { fetchTonBalance } from '../helpers/fetchTonBalance';
import { fetchCEXBalance } from '../helpers/fetchCEXBalance';

interface IUseTokenBalance {
  address: any;
  chainId: number;
  exchange?: string;
  formatUnits?: Unit;
  token: any;
  interval?: number;
  enabled?: boolean;
  onBalance?: (tokenBalance: BigNumber) => void;
}

const useTokenBalance = ({
  address,
  chainId,
  exchange,
  formatUnits,
  token,
  interval,
  enabled = true,
  onBalance,
}: IUseTokenBalance) => {
  const [tokenBalance, setTokenBalance] = useState<BigNumber>(BigNumber(0));

  const { isEVM, isElrond, isTon } = getNetworkFlags(chainId);

  const fetchEVM = async () => {
    const data = await fetchEVMBalance({
      address,
      chainId,
      formatUnits,
      token,
    });

    setTokenBalance(BigNumber(data.formatted));
  };

  const fetchElrond = async () => {
    const balance = await fetchElrondBalance({ address, token });

    setTokenBalance(balance);
  };

  const fetchTon = async () => {
    const balance = await fetchTonBalance({ address, token });

    setTokenBalance(balance);
  };

  const fetchCEX = async () => {
    const balance = await fetchCEXBalance(exchange!, token);

    setTokenBalance(balance);
  };

  const fetchBalance = () => {
    if (enabled && address) {
      if (exchange) {
        return fetchCEX();
      }
      if (isEVM) {
        return fetchEVM();
      }
      if (isElrond) {
        return fetchElrond();
      }
      if (isTon) {
        return fetchTon();
      }
    } else {
      setTokenBalance(BigNumber(0));
    }
  };

  useEffect(() => {
    let timer: number;

    fetchBalance();

    if (interval) {
      timer = window.setInterval(() => {
        fetchBalance();
      }, interval);
    }

    return () => {
      window.clearInterval(timer);
    };
  }, [address, chainId, formatUnits, token, enabled, interval, exchange]);

  useEffect(() => {
    if (onBalance) {
      onBalance(tokenBalance);
    }
  }, [tokenBalance]);

  return tokenBalance;
};

export default useTokenBalance;
